import { Link as RouterLink } from 'react-router-dom';
import { OutlinedLink } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { EntityIcon } from '@components/core-ui/EntityIcon/';
import { routes } from '@routes';
import { TickingDateAgo } from '@components/core-ui/Date/TickingDateAgo';
import DoubleDone from '@components/icons/DoubleDone.svg';
import CrossIcon from '@components/icons/Cross.svg';
import GearSvg from '@components/icons/Gear.svg';
import { TProps } from './types/TProps';

export const NotificationsPopover = ({
  notifications,
  isPending,
  onMarkAllRead,
  onOpenNotification,
  onDismissNotification,
}: TProps) => {
  return (
    <div className="overflow-hidden relative flex max-h-[calc(100dvh-6rem)] w-72 max-w-full cursor-default flex-col rounded-lg border border-border bg-surface text-foreground shadow-[0_12px_24px_var(--color-shadow)]">
      {isPending ? <AbsoluteSpinner overlay={true} /> : null}
      <div className="relative shrink-0 p-4">
        <h2 className="font-medium text-foreground-muted text-base leading-6 text-center">Notifications</h2>
        <RouterLink
          className="[&_svg]:text-foreground-muted [&_svg]:size-4 [&_svg]:hover:text-link absolute right-4 top-1/2 -translate-y-1/2"
          to={routes.notificationSettings.make()}
          aria-label="Notification settings"
        >
          <GearSvg />
        </RouterLink>
        <div className="h-px bg-border mx-4 absolute bottom-0 inset-x-0" aria-hidden />
      </div>

      {notifications.length > 0 ? (
        <>
          <ul className="p-4 min-h-[min(16rem,calc(100dvh-14rem))] overflow-y-auto space-y-4">
            {notifications.map((n) => {
              return (
                <li key={n.id} className="relative flex gap-3">
                  <EntityIcon
                    name={n.organizationName}
                    src={n.organizationImage}
                    sizeClassName="size-[45px] shrink-0 bg-[var(--color-avatar-bg)] text-on-surface text-xl"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-foreground-muted truncate">{n.organizationName}</p>
                    <RouterLink
                      to={n.url}
                      className="font-medium text-foreground hover:text-link line-clamp-3 text-[10px]"
                      onClick={() => onOpenNotification(n.id)}
                      title={n.message}
                    >
                      {n.message}
                      <span className="absolute inset-0" aria-hidden />
                    </RouterLink>
                    <p className="text-[10px] text-foreground-muted">
                      <TickingDateAgo date={n.createdAt} />
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 items-center">
                    <button
                      type="button"
                      className="relative grid place-items-center z-10 [&_svg]:size-3.5 size-4 p-0 text-foreground-muted hover:text-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDismissNotification(n.id);
                      }}
                      aria-label="Dismiss notification"
                    >
                      <CrossIcon />
                    </button>
                    {!n.seen ? (
                      <div role="img" aria-label="Unread" className="rounded-full size-1.5 bg-accent" />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="shrink-0 p-4 flex items-center justify-end gap-4">
            <button
              type="button"
              className="flex items-center gap-1 p-0 text-left text-xs text-foreground-muted font-medium hover:text-link hover:underline"
              onClick={onMarkAllRead}
            >
              Mark all as read
              <DoubleDone />
            </button>
            <OutlinedLink to={routes.notifications.make()}>View All</OutlinedLink>
          </div>
        </>
      ) : (
        <div className="p-4 h-72 flex items-center">
          <p className="text-sm text-foreground-muted text-center">
            {"You'll"} get notified here when someone mentions you, your job status updates, and more
          </p>
        </div>
      )}
    </div>
  );
};
